import BaseAuthProvider from './BaseAuthProvider'
import {AuthProviders} from '@/domain/login/constant/authProviders'

/**
 * Google OAuth authentication provider
 */
export default class GoogleAuthProvider extends BaseAuthProvider {
    /**
     * Load Google SDK
     */
    async loadSDK() {
        const googleClientId = process.env.VUE_APP_GOOGLE_APP_ID

        return new Promise((resolve) => {
            const script = document.createElement("script")
            script.src = "https://accounts.google.com/gsi/client"
            script.async = true
            script.defer = true
            script.onload = () => {
                // Initialize Google Identity Services
                window.google.accounts.id.initialize({
                    client_id: googleClientId,
                    auto_select: true
                })
                resolve()
            }
            document.body.appendChild(script)
        })
    }

    /**
     * Start Google login flow and get access token
     * @returns {Promise<string>} Access token
     */
    async startLogin() {
        const googleClientId = process.env.VUE_APP_GOOGLE_APP_ID

        const client = window.google.accounts.oauth2.initTokenClient({
            client_id: googleClientId,
            scope: 'profile email',
            callback: '' // defined at request time
        })

        return new Promise((resolve, reject) => {
            try {
                client.callback = (resp) => {
                    if (resp.error !== undefined) {
                        reject(resp)
                    } else {
                        resolve(resp.access_token)
                    }
                }
                client.requestAccessToken({prompt: 'consent'})
            } catch (err) {
                reject(err)
            }
        })
    }
    /**
     * Authenticate with Google OAuth token
     * @param {Object} credentials - {token, language?, allowRegister?}
     * @returns {Promise<Object>} {token: access_token, type: token_type, jwt}
     */
    async authenticate(credentials) {
        const {token, language, allowRegister = false} = credentials

        const params = {
            backend: 'google-oauth2', // Backend API expects this format
            token,
            grant_type: 'convert_token',
            client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
            is_dashboard: true
        }

        if (language) {
            params.language = language
        }

        if (allowRegister) {
            params.allow_register = true
        }

        const result = await this.service.convertAuthToken(params)

        return {
            token: result.access_token,
            type: result.token_type,
            jwt: result.jwt
        }
    }

    async logout(tokenData) {
        // Google auth doesn't need server-side logout
    }

    supportsRefresh() {
        return false
    }

    getMethod() {
        return AuthProviders.GOOGLE
    }
}
